import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { BigButton } from '../../components/BigButton';
import PasswordInput from '../../components/Inputs/PasswordInput';
import { GenericTextInput } from '../../components/Inputs/styles';
import {
    USER_MAINTENANCE_LOGIN_TOAST_ID,
    USER_MAINTENANCE_PARAMETER,
} from '../../consts/loginMaintenanceMessage';
import animations from '../../helpers/animations';
import { getErrorMessage } from '../../helpers/errorMessages';
import inputMasker from '../../helpers/inputMasker';
import authenticationService from '../../services/authentication.service';
import { useToken } from '../../store/auth/tokenStore';
import ForeignerContent from './ForeignerContent';
import {
    AuthModalContentProps,
    loginPageSchema,
    loginPageSchemaEn,
} from './logic';

type Fields = z.infer<typeof loginPageSchema>;

export default function LoginColumnContent({
    setCurrentColumn,
    setIsOpen,
}: AuthModalContentProps) {
    const { t, i18n } = useTranslation();

    const {
        register,
        handleSubmit,
        formState: { errors, isValid, isDirty },
        setError,
        clearErrors,
        setValue,
        trigger,
    } = useForm<Fields>({
        resolver: zodResolver(
            i18n.language === 'pt' ? loginPageSchema : loginPageSchemaEn
        ),
        mode: 'onChange',
    });
    const { setToken } = useToken();

    const [isLoading, setIsLoading] = useState(false);
    const [maintenanceNotice, setMaintenanceNotice] = useState(false);

    const isEnglishLanguage = useMemo(
        () => i18n.language !== 'pt',
        [i18n.language]
    );

    const handleLogin = async (data: Fields) => {
        setIsLoading(true);

        try {
            const authResult = await authenticationService.sendLoginRequest({
                login: data.login,
                password: data.password,
            });
            if (authResult.error === USER_MAINTENANCE_PARAMETER) {
                clearErrors('root');
                const body = t('login.maintenance.toast_full');
                toast.info(
                    <div
                        style={{ whiteSpace: 'pre-line', textAlign: 'left' }}
                    >
                        {body}
                    </div>,
                    {
                        toastId: USER_MAINTENANCE_LOGIN_TOAST_ID,
                        autoClose: 12_000,
                    }
                );
                setMaintenanceNotice(true);
                return;
            }
            if (authResult.error) {
                setMaintenanceNotice(false);
                return setError('root', {
                    message: getErrorMessage(authResult.error),
                });
            }
            setMaintenanceNotice(false);
            setToken(authResult.token!);
            setIsOpen(false);
        } finally {
            setIsLoading(false);
        }
    };

    const { onChange: onChangeLogin, ...restLogin } = register('login');

    useEffect(() => {
        setTimeout(() => {
            const login =
                document.querySelector<HTMLInputElement>('input[id="login"]')
                    ?.value || '';
            const password =
                document.querySelector<HTMLInputElement>('input[id="password"]')
                    ?.value || '';
            setValue('login', login);
            setValue('password', password);
            if (login && password) {
                trigger();
            }
        }, 100);
    }, [setValue, trigger]);

    return (
        <motion.form
            onSubmit={handleSubmit(handleLogin)}
            {...animations.fadeInOut(0.05)}
        >
            <div className="title-wrapper">
                <h2 className="title">{t('login.title_wrapper.title')}</h2>
                <p className="description">
                    {t('login.title_wrapper.description')}
                </p>
                <ForeignerContent />
            </div>
            <div className="text-inputs-wrapper">
                <div className="input-item">
                    <label htmlFor="cpf">
                        {t('login.text_inputs_wrapper.input_item.cpf_label')}
                    </label>
                    <GenericTextInput
                        id="login"
                        type="text"
                        placeholder={t(
                            'login.text_inputs_wrapper.input_item.cpf_placeholder'
                        )}
                        {...restLogin}
                        onChange={
                            isEnglishLanguage
                                ? onChangeLogin
                                : inputMasker.handleChange('CPFCNPJ')
                        }
                        maxLength={
                            isEnglishLanguage
                                ? 9
                                : inputMasker.getMaxLength('CPFCNPJ')
                        }
                        aria-invalid={Boolean(errors.login)}
                    />
                    {errors.login && <span>{errors.login.message}</span>}
                </div>
                <div className="input-item">
                    <label htmlFor="password">
                        {t(
                            'login.text_inputs_wrapper.input_item.password_label'
                        )}
                    </label>
                    <PasswordInput
                        inputProps={{
                            ...register('password'),
                            placeholder: t(
                                'login.text_inputs_wrapper.input_item.password_placeholder'
                            ),
                        }}
                        parentProps={{
                            'aria-invalid': Boolean(errors.password),
                        }}
                    />
                    {errors.password && <span>{errors.password.message}</span>}
                    <a
                        className="forgot-password"
                        onClick={() => setCurrentColumn('reset-password')}
                    >
                        {t(
                            'login.text_inputs_wrapper.input_item.forgot_password'
                        )}
                    </a>
                </div>
            </div>
            <div className="error-column">
                {maintenanceNotice && (
                    <div
                        className="login-maintenance-notice"
                        role="status"
                        aria-live="polite"
                    >
                        <p className="login-maintenance-notice__title">
                            {t('login.maintenance.title')}
                        </p>
                        <p className="login-maintenance-notice__summary">
                            {t('login.maintenance.summary')}
                        </p>
                    </div>
                )}
                {errors.root && (
                    <p className="api-error">
                        {errors.root.message!.split('\n').map(d => (
                            <>
                                {d}
                                <br />
                            </>
                        ))}
                    </p>
                )}
                <BigButton
                    bgColor={isDirty ? 'primary' : 'secondary'}
                    color={isDirty ? 'primary' : 'secondary'}
                    type="submit"
                    cursorBlocked={!isValid}
                    isLoading={isLoading}
                >
                    {t('login.big_button.submit_text')}
                </BigButton>
            </div>
            <p className="create-account-notice">
                {t('login.create_account_notice.text')}{' '}
                <a onClick={() => setCurrentColumn('signup')}>
                    {t('login.create_account_notice.link_text')}
                </a>
            </p>
        </motion.form>
    );
}
